// File: voces-de-esperanza/src/utils/siteUrls.js
// Fuente única del dominio base y de la traducción de rutas públicas entre los dos sitios
// hermanos (VDE/es, VOH/en). Antes de esto había 3 copias sueltas del dominio base
// (Layout.js SITE_ES_URL/SITE_EN_URL, Seo.js SITE.*.url, gatsby-config.js siteMetadata.siteUrl
// con "www." de más) que podían divergir sin avisar — quedan unificadas aquí.
//
// Alcance de PUBLIC_SLUG_MAP: solo las 6 secciones públicas acordadas (Devocionales, Formación,
// Ministerio, Historias, Equipo, Tienda) tienen slug en inglés en voices-of-hope.com. La
// plataforma autenticada (mi-cuenta, ajustes, historial, estudios/*, mentor*, admin*,
// discipulo*, fruto/certificado, fruto/pastoral, reportar, upload) se queda igual en los dos
// sitios a propósito: Firebase Auth guarda la sesión por origin, así que cambiar de dominio ahí
// desloguearía al usuario, y no es contenido que se comparta públicamente. /fruto no se traduce
// porque es el nombre del método de Phil (el propio translation.json en inglés lo deja como
// "FRUTO"). /3for3 tampoco, ya está fuera de alcance de i18n por decisión de homepage-ia.
//
// module.exports (no `export`) a propósito: gatsby-config.js y gatsby-node.js corren en Node
// plano (sin Babel) y lo cargan con require(); mismo patrón que projectLogParser.js. Los
// componentes React lo consumen con `import { x } from '../utils/siteUrls'` — Babel resuelve
// el interop de sobra (ver admin/desarrollo.js importando projectLogParser del mismo modo).

const SITE_URLS = {
  es: "https://voces-de-esperanza.com",
  en: "https://voices-of-hope.com",
};

// Ordenado del prefijo más largo al más corto — /ministerio/historias debe matchear antes que
// /ministerio, si no la traducción del padre se come el segmento hijo.
const PUBLIC_SLUG_MAP = [
  ["/ministerio/historias", "/ministry/stories"],
  ["/ministerio", "/ministry"],
  ["/devocionales", "/devotionals"],
  ["/formacion", "/formation"],
  ["/equipo", "/team"],
  ["/tienda", "/store"],
];

const PROTECTED_PREFIXES = [
  "/mi-cuenta",
  "/ajustes",
  "/historial",
  "/estudios",
  "/mentor",
  "/admin",
  "/discipulo",
  "/fruto/certificado",
  "/fruto/pastoral",
  "/reportar",
  "/upload",
];

function isProtectedPath(pathname) {
  return PROTECTED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// Traduce solo el segmento público conocido; conserva el resto del path (slug dinámico,
// trailing slash) intacto. Devuelve el mismo pathname sin tocar si es una ruta protegida o si
// no matchea ningún segmento del mapa (home "/", /fruto/, /3for3/, etc.).
function translatePublicPath(pathname, targetLang) {
  if (!pathname || isProtectedPath(pathname)) return pathname;

  for (const [es, en] of PUBLIC_SLUG_MAP) {
    if (targetLang === "en" && pathname.startsWith(es)) {
      return en + pathname.slice(es.length);
    }
    if (targetLang === "es" && pathname.startsWith(en)) {
      return es + pathname.slice(en.length);
    }
  }

  return pathname;
}

function getSiblingUrl(pathname, currentLang) {
  const targetLang = currentLang === "en" ? "es" : "en";
  return localizedUrl(pathname, targetLang);
}

// URL absoluta de un path en el sitio de `targetLang`. A diferencia de getSiblingUrl no
// invierte nada: recibe el idioma destino explícito, que es lo que necesita el toggle para
// no depender del idioma que hay en memoria (ver getBuildLanguage abajo).
function localizedUrl(pathname, targetLang) {
  const lang = targetLang === "en" ? "en" : "es";
  return `${SITE_URLS[lang]}${translatePublicPath(pathname, lang)}`;
}

/**
 * Idioma del BUILD que se está sirviendo — no el del toggle en memoria.
 *
 * Es la distinción que faltaba y que causaba el bug de S20: cada dominio sirve un solo
 * build, y `onCreatePage` (gatsby-node.js) *reemplaza* las rutas públicas por su slug en
 * inglés en el build EN. O sea: /devotionals/ existe únicamente en voices-of-hope.com y
 * /devocionales/ únicamente en voces-de-esperanza.com. Un link interno calculado con el
 * idioma del toggle apuntaba a una ruta que no existe en este build, y el rewrite `**` de
 * Firebase la servía como index.html → el usuario "volvía al home" en cada clic.
 *
 * Misma regla que ya seguían newsletter-service.js y Seo.js: en web el idioma se infiere
 * del dominio; en Node, del flag del build. Los dos coinciden siempre, así que el valor es
 * estable entre SSR e hidratación (a diferencia de `language`, que la preferencia guardada
 * podía voltear después de hidratar).
 *
 * Host desconocido (localhost, previews raros, webview nativo): cae a 'es', que es el build
 * por defecto de `gatsby develop`.
 */
function getBuildLanguage() {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("voices-of-hope")) return "en";
    if (hostname.includes("voces-de-esperanza")) return "es";
    return "es";
  }
  return process.env.SITE_ENGLISH_BUILD === "true" ? "en" : "es";
}

/**
 * Path de una ruta pública TAL COMO EXISTE EN ESTE BUILD. Es lo que debe usar cualquier
 * `<Link to>` / `navigate()` interno; `translatePublicPath(path, lang)` con idioma explícito
 * se reserva para URLs absolutas del otro sitio (canonical, hreflang, links para compartir).
 */
function buildPath(pathname) {
  return translatePublicPath(pathname, getBuildLanguage());
}

module.exports = {
  SITE_URLS,
  PUBLIC_SLUG_MAP,
  PROTECTED_PREFIXES,
  isProtectedPath,
  translatePublicPath,
  getSiblingUrl,
  localizedUrl,
  getBuildLanguage,
  buildPath,
};
